import { useEffect, useRef } from 'react';

// GLI ALBERI ENTRANO APERTI E SI RICORDANO QUELLO CHE CHIUDI (utente, 2026-10-16). Objects e Materials nello
// Scene Editor, la struttura nel Composer: al primo accesso tutto aperto, e i rami chiusi restano chiusi al
// prossimo accesso a quel prodotto. Si scrivono solo i chiusi, così un ramo nuovo nasce aperto.
//
// PER PRODOTTO E NEL BROWSER, come le sezioni chiuse dell'Inspector (`propertiesPanelSectionMemory`): è una
// comodità di chi lavora, non un dato del prodotto. Ogni albero dice con che chiave riconoscere un ramo da
// una sessione all'altra — l'id di una mesh nel motore cambia a ogni caricamento, il suo `id` salvato no.

const STORAGE_KEY = 'zakeke.treeBranches.closed';

type Store = Record<string, string[]>;

const readStore = (): Store => {
    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
        return stored && typeof stored === 'object' ? (stored as Store) : {};
    } catch {
        return {};
    }
};

const writeStore = (store: Store) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // Senza il browser che ricorda, l'albero entra aperto ogni volta: come prima.
    }
};

export const useRememberedBranches = ({
    scope,
    branchIds,
    open,
    setOpen,
    keyOf = (id) => id
}: {
    /** Quale albero di quale prodotto: «scene-objects:123». Senza, non si ricorda niente. */
    scope: string | null;
    /** I rami che si possono aprire, come li conosce l'albero adesso. */
    branchIds: string[];
    open: Set<string>;
    setOpen: (open: Set<string>) => void;
    /** Come riconoscere un ramo al prossimo accesso. */
    keyOf?: (id: string) => string;
}) => {
    const restored = useRef(false);
    // Nel giro in cui si rimette lo stato, `open` è ancora quello di prima: scriverlo cancellerebbe la memoria.
    const justRestored = useRef(false);

    // AL PRIMO ELENCO PIENO: tutto aperto, tranne ciò che era stato chiuso.
    useEffect(() => {
        if (restored.current || !branchIds.length) return;
        restored.current = true;
        justRestored.current = true;
        const closed = new Set(scope ? (readStore()[scope] ?? []) : []);
        setOpen(new Set(branchIds.filter((id) => !closed.has(keyOf(id)))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchIds.length]);

    // DA LÌ IN POI SI SCRIVE OGNI CAMBIO. I rami che adesso non ci sono — filtrati, o di un'altra vista —
    // tengono la loro scelta: si riscrive solo la parte che l'albero vede.
    useEffect(() => {
        if (!restored.current || !scope) return;
        if (justRestored.current) {
            justRestored.current = false;
            return;
        }
        const store = readStore();
        const present = new Set(branchIds.map(keyOf));
        const kept = (store[scope] ?? []).filter((key) => !present.has(key));
        const closedNow = branchIds.filter((id) => !open.has(id)).map(keyOf);
        writeStore({ ...store, [scope]: [...kept, ...closedNow] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
};
